import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Divider, Paper, Theme,
} from '@mui/material';
import { useLocation } from 'react-router-dom';

import { FLOATING_ELEVATION } from '../../theme/shadows';
import { RESIZE_CURSORS, ResizeEdge } from '../../hooks/useResizablePanel';
import { PanelSize } from './types';
import panelModes, { PanelMode } from './panelModes';
import { useDockContext } from './PinguDockContext';
import { PinguChat } from '../../hooks/usePinguChat';
import usePinguSessions from '../../hooks/usePinguSessions';
import { currentConversationSpend, sumSessionSpend } from './conversationSpend';
import PinguChatHeader from './PinguChatHeader';
import PinguSessionsSidebar from './PinguSessionsSidebar';
import PinguMessageList, { EmptyStateMeasurement } from './PinguMessageList';
import PinguStartingPrompts from './PinguStartingPrompts';
import PinguComposer, { PinguComposerHandle } from './PinguComposer';
import PinguCreditsMeter from './PinguCreditsMeter';
import PinguIncludePageChip from './PinguIncludePageChip';
import PinguPageContextChip from './PinguPageContextChip';
import PinguDockedResizeEdge from './PinguDockedResizeEdge';

const EDGE_HANDLE_PX = 6;
const CORNER_HANDLE_PX = 12;

const NARROW_PANEL_WIDTH = 560;

const RESIZE_HANDLES: { edge: ResizeEdge; sx: Record<string, number> }[] = [
  {
    edge: 'n',
    sx: {
      top: 0, left: 0, right: 0, height: EDGE_HANDLE_PX,
    },
  },
  {
    edge: 's',
    sx: {
      bottom: 0, left: 0, right: 0, height: EDGE_HANDLE_PX,
    },
  },
  {
    edge: 'w',
    sx: {
      top: 0, bottom: 0, left: 0, width: EDGE_HANDLE_PX,
    },
  },
  {
    edge: 'e',
    sx: {
      top: 0, bottom: 0, right: 0, width: EDGE_HANDLE_PX,
    },
  },
  {
    edge: 'nw',
    sx: {
      top: 0, left: 0, width: CORNER_HANDLE_PX, height: CORNER_HANDLE_PX,
    },
  },
  {
    edge: 'ne',
    sx: {
      top: 0, right: 0, width: CORNER_HANDLE_PX, height: CORNER_HANDLE_PX,
    },
  },
  {
    edge: 'sw',
    sx: {
      bottom: 0, left: 0, width: CORNER_HANDLE_PX, height: CORNER_HANDLE_PX,
    },
  },
  {
    edge: 'se',
    sx: {
      bottom: 0, right: 0, width: CORNER_HANDLE_PX, height: CORNER_HANDLE_PX,
    },
  },
];

const PinguChatWindow = ({
  chat,
  size,
  position,
  minSize,
  onStartMove,
  onStartResize,
  onClose,
  mode = panelModes.FLOATING,
  onToggleMode,
}: {
  chat: PinguChat;
  size: PanelSize;
  position: { right: number; bottom: number };
  minSize: PanelSize;
  onStartMove: (event: React.MouseEvent<HTMLElement>) => void;
  onStartResize: (event: React.MouseEvent<HTMLElement>, edge: ResizeEdge) => void;
  onClose: () => void;
  mode?: PanelMode;
  onToggleMode?: () => void;
}) => {
  const docked = mode === panelModes.DOCKED;
  const { dockedWidth } = useDockContext();
  const panelWidth = docked ? dockedWidth : size.width;

  const [sessionsSidebarOpen, setSessionsSidebarOpen] = useState(false);
  const [showArchivedSessions, setShowArchivedSessions] = useState(false);
  const [creditsOpen, setCreditsOpen] = useState(false);
  const composerRef = useRef<PinguComposerHandle>(null);
  const historyButtonRef = useRef<HTMLButtonElement>(null);
  const [pageContextDismissed, setPageContextDismissed] = useState(false);
  const [emptyState, setEmptyState] = useState<EmptyStateMeasurement | null>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    setPageContextDismissed(false);
  }, [pathname]);

  const showPageContext = !pageContextDismissed;

  // The page tag and "Include this page" replace each other, so focus would
  // fall to the body. It moves to the new chip, but only if the old one had
  // it, so a mouse click shows no focus ring.
  const contextChipRef = useRef<HTMLDivElement>(null);
  const refocusContextChipRef = useRef(false);

  const handlePageContextDismissed = (dismissed: boolean) => {
    refocusContextChipRef.current = Boolean(
      contextChipRef.current?.contains(document.activeElement),
    );
    setPageContextDismissed(dismissed);
  };

  useEffect(() => {
    if (!refocusContextChipRef.current) return;
    refocusContextChipRef.current = false;
    contextChipRef.current?.focus();
  }, [pageContextDismissed]);

  const handleSend = (text: string, files: File[] = []) => chat.sendMessage(text, {
    ...(showPageContext ? { pageContext: pathname } : {}),
  }, files);

  const {
    sessions,
    fetchMoreSessions,
    sessionsHasMore,
    sessionsLoading,
    sessionsRefreshing,
    sessionsLoadingMore,
    sessionsCapped,
    sessionsPageCount,
    refetchSessions,
    resetSessionsPagination,
  } = usePinguSessions(sessionsSidebarOpen || creditsOpen, showArchivedSessions);

  useEffect(() => {
    if (sessionsSidebarOpen && chat.sessionId) refetchSessions();
  }, [chat.sessionId, refetchSessions, sessionsSidebarOpen]);

  // Below this the list and the transcript cannot both hold their minimum, so
  // the list takes the panel and its own hide control is the way back.
  const listTakesPanel = sessionsSidebarOpen && panelWidth < NARROW_PANEL_WIDTH;

  const handleToggleSessions = async () => {
    const willOpen = !sessionsSidebarOpen;
    setSessionsSidebarOpen(willOpen);
    if (willOpen) await refetchSessions();
  };

  const handleHideSessions = () => {
    setSessionsSidebarOpen(false);
    requestAnimationFrame(() => historyButtonRef.current?.focus());
  };

  const handleOpenCredits = async () => {
    setCreditsOpen(true);
    await refetchSessions();
  };

  const handleToggleArchivedSessions = () => {
    resetSessionsPagination();
    setShowArchivedSessions((previous) => !previous);
  };

  const handleSelectSession = async (selectedSessionId: string) => {
    const narrow = panelWidth < NARROW_PANEL_WIDTH;
    if (narrow) setSessionsSidebarOpen(false);
    await chat.loadSession(selectedSessionId);
    if (narrow) requestAnimationFrame(() => composerRef.current?.focus());
  };

  const handleStartNewSession = () => {
    const narrow = panelWidth < NARROW_PANEL_WIDTH;
    if (narrow) setSessionsSidebarOpen(false);
    chat.startNewSession();
    if (narrow) requestAnimationFrame(() => composerRef.current?.focus());
  };

  const handleRenameSession = async (sessionId: string, name: string): Promise<boolean> => {
    const renamed = await chat.renameSession(sessionId, name);
    if (renamed) await refetchSessions();
    return renamed;
  };

  const handleArchiveSession = async (sessionId: string, isArchived: boolean): Promise<boolean> => {
    const archived = await chat.archiveSession(sessionId, isArchived);
    if (archived) await refetchSessions();
    return archived;
  };

  const placementSx = docked ? {
    top: 0,
    right: 0,
    bottom: 0,
    zIndex: (theme: Theme) => theme.zIndex.pinguDockedPanel,
    width: dockedWidth,
    borderRadius: 0,
    borderLeft: (theme: Theme) => `1px solid ${theme.palette.divider}`,
  } : {
    right: position.right,
    bottom: position.bottom,
    zIndex: (theme: Theme) => theme.zIndex.pinguPanel,
    width: size.width,
    height: size.height,
    minWidth: minSize.width,
    minHeight: minSize.height,
    maxWidth: 'calc(100vw - 24px)',
    maxHeight: 'calc(100vh - 24px)',
    borderRadius: 2,
    border: (theme: Theme) => `1px solid ${theme.palette.divider}`,
  };

  return (
    <>
      <Paper
        elevation={docked ? 0 : FLOATING_ELEVATION}
        sx={{
          position: 'fixed',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          ...placementSx,
        }}
      >
        <PinguChatHeader
          sessionName={chat.sessionName}
          canRename={Boolean(chat.sessionId)}
          onRename={(name) => (chat.sessionId
            ? chat.renameSession(chat.sessionId, name)
            : Promise.resolve(false))}
          onStartMove={onStartMove}
          onStartNewSession={handleStartNewSession}
          onToggleSessions={handleToggleSessions}
          onClose={onClose}
          historyButtonRef={historyButtonRef}
          docked={docked}
          onToggleMode={onToggleMode}
        />

        {/* Docked, the header draws its own border to line up with the navbar. */}
        {!docked && <Divider />}

        <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
          {sessionsSidebarOpen && (
            <PinguSessionsSidebar
              sessions={sessions}
              archivedMode={showArchivedSessions}
              fullWidth={listTakesPanel}
              hasMore={Boolean(sessionsHasMore)}
              loading={sessionsLoading}
              loadingMore={sessionsLoadingMore}
              pageCount={sessionsPageCount}
              currentSessionId={chat.sessionId}
              sessionProcessingStatuses={chat.sessionProcessingStatuses}
              onArchiveSession={handleArchiveSession}
              onFetchMore={() => { fetchMoreSessions(); }}
              onHide={handleHideSessions}
              onResetPagination={resetSessionsPagination}
              onSelectSession={handleSelectSession}
              onToggleArchivedMode={handleToggleArchivedSessions}
              onRenameSession={handleRenameSession}
            />
          )}

          <Box sx={{
            display: listTakesPanel ? 'none' : 'flex',
            flexDirection: 'column',
            flex: 1,
            minWidth: 0,
          }}
          >
            <PinguMessageList
              messages={chat.messages}
              loading={chat.loading && !chat.loadingSession}
              activeSessionId={chat.sessionId}
              onMessageTyped={chat.markMessageTyped}
              onMessageFeedback={chat.submitMessageFeedback}
              onWithdrawFeedback={chat.withdrawMessageFeedback}
              onRetry={chat.retryLastMessage}
              onCheckAgain={() => {
                if (chat.sessionId) chat.loadSession(chat.sessionId);
              }}
              mode={mode}
              onEmptyStateMeasured={setEmptyState}
            />

            {chat.messages.length === 0 && (
              <PinguStartingPrompts
                draw={chat.startingPrompts}
                onSelect={handleSend}
                showMark={!(emptyState?.largeMarkFits ?? true)}
                transcript={emptyState?.transcript}
              />
            )}

            <PinguComposer
              ref={composerRef}
              disabled={chat.loading}
              canCancel={chat.loading && !chat.loadingSession && Boolean(chat.sessionId)}
              contextChip={showPageContext ? (
                <PinguPageContextChip
                  ref={contextChipRef}
                  pathname={pathname}
                  onDismiss={() => handlePageContextDismissed(true)}
                />
              ) : (
                <PinguIncludePageChip
                  ref={contextChipRef}
                  onInclude={() => handlePageContextDismissed(false)}
                />
              )}
              onSend={handleSend}
              onCancel={chat.cancelSession}
              creditsMeter={(
                <PinguCreditsMeter
                  loading={sessionsLoading || sessionsRefreshing}
                  conversationSpend={
                    currentConversationSpend(chat.costBreakdown, chat.sessionId, sessions)
                  }
                  allConversationsSpend={sumSessionSpend(sessions)}
                  sessionCount={sessions.length}
                  sessionsCapped={sessionsCapped}
                  onOpen={() => { handleOpenCredits(); }}
                  onClose={() => setCreditsOpen(false)}
                />
              )}
            />
          </Box>
        </Box>

        {!docked && RESIZE_HANDLES.map(({ edge, sx }) => (
          <Box
            key={edge}
            onMouseDown={(event) => onStartResize(event, edge)}
            sx={{
              ...sx,
              position: 'absolute',
              zIndex: 1,
              cursor: RESIZE_CURSORS[edge],
            }}
          />
        ))}
      </Paper>
      {docked && <PinguDockedResizeEdge />}
    </>
  );
};

export default PinguChatWindow;
